import { useCallback, useEffect, useState } from 'react'
import {
  ApolloClient,
  gql,
  NormalizedCacheObject,
  QueryResult,
  useApolloClient,
  useMutation,
  useQuery,
} from '@apollo/client'
import axios, { AxiosError } from 'axios'
import { getCookie } from 'cookies-next'
import Stripe from 'stripe'
import { AUTH_JWT_COOKIE } from '@/constants/cookies'
import {
  CreateGuestPaypalPurchaseMutation,
  CreateGuestPaypalPurchaseMutationVariables,
  CreatePaypalPurchaseMutation,
  CreatePaypalPurchaseMutationVariables,
  GetOrdersAndMarketSubscriptionsQuery,
  GuestPaypalPaymentInput,
  PaypalPaymentInput,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import type { OrdersAndSubscriptions, Subscription } from './types'
import { isActiveSubscription, isGuildSubscription, snakeToCamel } from './utils'

export type { Order, OrdersAndSubscriptions, Perk, Subscription, SubscriptionInterval } from './types'
export { PaymentStatus, OrderType } from './types'

const getOrdersAndMarketSubscriptions = gql`
  query getOrdersAndMarketSubscriptions {
    orders {
      created
      currency
      id
      project {
        slug
        title
      }
      price
      paidPrice
      status
      subscriptionrecordSet {
        id
      }
    }
    marketSubscriptions {
      CanceledAt
      created
      currency
      end
      id
      interval
      name
      offerSnapshot
      price
      platformSubscriptionId
      project {
        slug
        title
      }
      status
    }
  }
`

export const GET_SUBSCRIPTIONS = gql`
  query getSubscriptions {
    marketSubscriptions {
      CanceledAt
      created
      currency
      end
      id
      interval
      name
      offerSnapshot
      price
      platformSubscriptionId
      project {
        slug
        title
      }
      status
    }
  }
`

export const CREATE_GUEST_PAYPAL_PURCHASE = gql`
  mutation createGuestPaypalPurchase($input: GuestPaypalPaymentInput!) {
    guestPaypalPurchase(input: $input) {
      order {
        id
        user {
          email
          id
        }
      }
      userCreatedWithOrder
      userToken
    }
  }
`

export const CREATE_PAYPAL_PURCHASE = gql`
  mutation createPaypalPurchase($input: PaypalPaymentInput!) {
    paypalPurchase(input: $input) {
      order {
        id
      }
    }
  }
`

export const PAYMENT_PORTAL_MUTATION = gql`
  mutation createGuildStripeBillingPortal($input: GuildStripeBillingPortalInput) {
    createGuildStripeBillingPortal(input: $input) {
      url
    }
  }
`

interface Card {
  brand: string
  last4: string
  expMonth: number
  expYear: number
  funding: string
  country: string
}

interface Coupon {
  amountOff: number | null
  appliesTo: string | null
  duration: string
  durationInMonths: number | null
  id: string
  maxRedemptions: number | null
  name: string
  percentOff: number | null
  redeemBy: number | null
  valid: boolean
}

interface Plan {
  active: boolean
  amount: number
  billingScheme: string
  id: string
  interval: 'year' | 'month'
  intervalCount: number
  metadata: {
    context: string
    offerSlug: string
  }
}

interface ProrationDetails {
  amount: number
  amountExcludingTax: number
  currency: string
  description: string
  period: {
    start: number
    end: number
  }
  plan: Plan
}

export interface StripeQuote {
  card: Card | null
  coupon: Coupon | null
  currency: string
  prorationDetails: ProrationDetails[]
  subscriptionProrationDate: number
  total: number
}

interface CreateStripeQuote {
  authToken: string
  currentStripeSubscriptionId: string
  newPriceId: string
  couponId?: string
  customerCode?: string
}

export const createStripeQuote = async ({
  authToken,
  currentStripeSubscriptionId,
  newPriceId,
  couponId,
  customerCode,
}: CreateStripeQuote): Promise<StripeQuote | null> => {
  const response = await axios
    .post(
      `${process.env.NEXT_PUBLIC_BFF_API_URL}/payments/create-stripe-quote`,
      {
        current_stripe_subscription_id: currentStripeSubscriptionId,
        new_price_id: newPriceId,
        coupon_id: couponId,
        customer_code: customerCode,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      },
    )
    .catch(async (error: AxiosError) => {
      // retry without coupon
      if (error.response?.status === 400) {
        return axios.post(
          `${process.env.NEXT_PUBLIC_BFF_API_URL}/payments/create-stripe-quote`,
          {
            current_stripe_subscription_id: currentStripeSubscriptionId,
            new_price_id: newPriceId,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: authToken,
            },
          },
        )
      } else {
        throw error
      }
    })
    .catch((error) => {
      logger().error(
        'Error creating Stripe quote',
        {
          currentStripeSubscriptionId,
          newPriceId,
          couponId,
          customerCode,
        },
        error,
      )
    })

  return (snakeToCamel(response?.data) as unknown as StripeQuote) || null
}

export const collectClaimCode = async (code: string) => {
  try {
    const authToken = getCookie(AUTH_JWT_COOKIE)
    if (!authToken) {
      throw new Error('No auth token found')
    }
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_USER_ADMIN_URL}/api/self/claim-code/${code}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      },
    )
    return response
  } catch (error) {
    logger().error('Claim code error', { error })
    return null
  }
}

interface UseStripeQuote {
  initialQuote: StripeQuote | null
}

export function useStripeQuote({ initialQuote }: UseStripeQuote) {
  const [quote, setQuote] = useState(initialQuote)
  const [error, setError] = useState('')
  const [loadingQuote, setLoadingQuote] = useState(false)
  const authToken = getCookie(AUTH_JWT_COOKIE)

  const getQuote = useCallback(
    async (input: Omit<CreateStripeQuote, 'authToken'>) => {
      setError('') // reset error upon submission
      if (typeof authToken !== 'string') {
        setError('Please try logging out and back in to proceed.')
        return
      }

      setLoadingQuote(true)
      try {
        const quote = (await createStripeQuote({ ...input, authToken })) as unknown as StripeQuote
        setQuote(quote)
        if (input.customerCode && !quote.coupon?.valid) {
          setError('Invalid discount code')
        }
        return quote
      } catch (error) {
        setError('Something went wrong. Please try again.')
        logger().error('Error creating Stripe quote', { input }, error)
      } finally {
        setLoadingQuote(false)
      }
    },
    [authToken],
  )

  return { error, getQuote, loadingQuote, quote, setError }
}

interface AcceptStripeQuote {
  authToken: string
  couponId?: string
  platformSubscriptionId: string
  newPriceId: string
}

export const acceptStripeQuote = async ({
  authToken,
  couponId,
  platformSubscriptionId,
  newPriceId,
}: AcceptStripeQuote) => {
  return axios.post(
    `${process.env.NEXT_PUBLIC_BFF_API_URL}/payments/accept-stripe-quote`,
    {
      current_stripe_subscription_id: platformSubscriptionId,
      new_price_id: newPriceId,
      coupon_id: couponId,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: authToken,
      },
    },
  )
}

export const getActiveGuildSubscription = async (client: ApolloClient<NormalizedCacheObject>) => {
  const subscriptions: Subscription[] = await client
    .query({
      query: GET_SUBSCRIPTIONS,
    })
    .then((result) => {
      return result.data.marketSubscriptions
    })
  return subscriptions.find((s: Subscription) => isActiveSubscription(s) && isGuildSubscription(s))
}

export async function getUpcomingStripeInvoice(
  stripeSubscriptionId: string,
): Promise<Stripe.UpcomingInvoice | undefined> {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_BFF_API_URL}/payments/upcoming-invoice`, {
      params: { subscription_id: stripeSubscriptionId },
    })
    if (response.data.message) {
      // If message is present, then the invoice was not available. This message explains why.
      return
    }
    return response.data
  } catch (err) {
    // Do nothing. This api route may throw if an invoice is not available.
  }
}

export const useCreateGuestPaypalPurchase = (bimClient?: ApolloClient<object>) => {
  const [mutate] = useMutation<CreateGuestPaypalPurchaseMutation, CreateGuestPaypalPurchaseMutationVariables>(
    CREATE_GUEST_PAYPAL_PURCHASE,
    {
      client: bimClient,
      errorPolicy: 'all',
    },
  )

  const createGuestPaypalPurchase = useCallback(
    (input: GuestPaypalPaymentInput) => mutate({ variables: { input } }),
    [mutate],
  )

  return {
    createGuestPaypalPurchase,
  }
}

export const useCreatePaypalPurchase = (bimClient?: ApolloClient<object>) => {
  const [mutate] = useMutation<CreatePaypalPurchaseMutation, CreatePaypalPurchaseMutationVariables>(
    CREATE_PAYPAL_PURCHASE,
    {
      client: bimClient,
      errorPolicy: 'all',
    },
  )

  const createPaypalPurchase = useCallback(
    (input: PaypalPaymentInput) => {
      mutate({ variables: { input } })
    },
    [mutate],
  )

  return {
    createPaypalPurchase,
  }
}

export const useUpcomingStripeInvoice = (stripeSubscriptionId?: string) => {
  const [upcomingInvoice, setUpcomingInvoice] = useState<Stripe.UpcomingInvoice>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    async function getUpcomingInvoice() {
      if (!stripeSubscriptionId) return

      setLoading(true)
      try {
        const invoice = await getUpcomingStripeInvoice(stripeSubscriptionId)
        if (invoice) setUpcomingInvoice(invoice)
      } catch (err) {
        // do nothing
      } finally {
        setLoading(false)
      }
    }
    getUpcomingInvoice()
  }, [stripeSubscriptionId])

  return { upcomingInvoice, loading }
}

export const useOrdersAndSubscriptions = (): QueryResult<OrdersAndSubscriptions> => {
  const client = useApolloClient()
  return useQuery(getOrdersAndMarketSubscriptions, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
    errorPolicy: 'all',
  })
}

export const getOrdersAndSubscriptions = (client: ApolloClient<NormalizedCacheObject>) => {
  return client.query<GetOrdersAndMarketSubscriptionsQuery>({
    query: getOrdersAndMarketSubscriptions,
  })
}
